<template>
  <div v-if="companyItemTypes">
    <table class="table is-bordered is-narrow is-fullwidth"
      :class="{ 'is-loading' : isFetchingSupplementaryQuote }">
      <thead>
        <tr class="no-border"
          :class="`summary-background-text-${$theme.name}`">
          <th>Category</th>
          <template v-if="value.subQuoteNos.length > 1 && value.subQuoteNo === 0">
            <th v-for="(sub) in value.subQuoteNos"
              :key="sub.value"
              class="has-text-right is-subtotal">
              {{ sub.key === 0 ? 'Main' : `Add-${sub.key}` }}
            </th>
            <th class="has-text-right is-subtotal">
              Summary
            </th>
          </template>
          <template v-else-if="value.subQuoteNos.length === 1 || value.subQuoteNo > 0">
            <th class="has-text-right is-subtotal">
              Total
            </th>
          </template>
        </tr>
      </thead>
      <tfoot v-if="value.subQuoteNos.length === 1 || (showSupplementaryQuoteTotals) || value.subQuoteNo > 0"
        class="no-border">
        <tr class="has-border-bottom-4"
          :class="[`summary-background-text-${$theme.name}`]">
          <td>Total <span class="subtext">(Ex GST)</span></td>
          <td class="has-text-right">{{ $filters.formatCurrency(value.totalExGst, $userInfo.locale) }}</td>
          <template v-if="showSupplementaryQuoteTotals">
            <td v-for="(supp) in supplementaryQuotes"
              :key="supp.quoteId"
              class="has-text-right">
              {{ $filters.formatCurrency(supp.totalExGst, $userInfo.locale) }}
            </td>
            <td class="has-text-right">{{ $filters.formatCurrency(calculateGrandTotal(value.totalExGst, supplementaryQuotes.map(q => q.totalExGst)), $userInfo.locale) }}</td>
          </template>
        </tr>
        <tr v-if="value.discount">
          <td>Discount</td>
          <td class="has-text-right has-text-danger">- {{ $filters.formatCurrency(value.discount, $userInfo.locale) }}</td>
          <template v-if="showSupplementaryQuoteTotals">
            <td v-for="(supp) in supplementaryQuotes"
              :key="supp.quoteId"
              class="has-text-right">-
            </td>
            <td class="has-text-right has-text-danger">- {{ $filters.formatCurrency(value.discount, $userInfo.locale) }}</td>
          </template>
        </tr>
        <tr v-if="value.excess && value.excessWithGst">
          <td>Excess</td>
          <td class="has-text-right has-text-danger">- {{ $filters.formatCurrency(value.excess, $userInfo.locale) }}</td>
          <template v-if="showSupplementaryQuoteTotals">
            <td v-for="(supp) in supplementaryQuotes"
              :key="supp.quoteId"
              class="has-text-right">-
            </td>
            <td class="has-text-right has-text-danger">- {{ $filters.formatCurrency(value.excess, $userInfo.locale) }}</td>
          </template>
        </tr>
        <tr class="has-border-bottom-4"
          :class="[`summary-background-primary-${$theme.name}`]">
          <td>GST <span class="subtext">({{ value.gstRate }}%)</span></td>
          <template v-if="showSupplementaryQuoteTotals">
            <td class="has-text-right"><span v-if="mainQuoteTotalGst > 0">+</span>{{ $filters.formatCurrency(mainQuoteTotalGst, $userInfo.locale) }}</td>
            <td v-for="(supp) in supplementaryQuotes"
              :key="supp.quoteId"
              class="has-text-right">
              +{{ $filters.formatCurrency((supp.totalExGst * supp.gstRate/100), $userInfo.locale) }}
            </td>
          </template>
          <td class="has-text-right"><span v-if="invTotalExGst > 0">+</span>{{ $filters.formatCurrency(invTotalExGst * (value.gstRate/100), $userInfo.locale) }}</td>
        </tr>
        <tr v-if="showInvAmtIncGst"
          :class="`summary-background-text-${$theme.name}`">
          <td>Total <span class="subtext">(Inc GST)</span></td>
          <template v-if="showSupplementaryQuoteTotals">
            <td class="has-text-right">{{ $filters.formatCurrency(mainQuoteTotalIncGst, $userInfo.locale) }}</td>
            <td v-for="(supp) in supplementaryQuotes"
              :key="supp.quoteId"
              class="has-text-right">
              {{ $filters.formatCurrency(supp.totalIncGst, $userInfo.locale) }}
            </td>
          </template>
          <td class="has-text-right">{{ $filters.formatCurrency(invTotalExGst * (value.gstRate/100 + 1), $userInfo.locale) }}</td>
        </tr>
        <tr v-if="value.ownersContribution">
          <td>Owner Cont. <span class="subtext">(Inc GST)</span></td>
          <template v-if="showSupplementaryQuoteTotals">
            <td class="has-text-right has-text-danger">- {{ $filters.formatCurrency(value.ownersContribution * (value.gstRate/100 + 1), $userInfo.locale) }}</td>
            <td v-for="(supp) in supplementaryQuotes"
              :key="supp.quoteId"
              class="has-text-right">-
            </td>
          </template>
          <td class="has-text-right has-text-danger">- {{ $filters.formatCurrency(value.ownersContribution * (value.gstRate/100 + 1), $userInfo.locale) }}</td>
        </tr>
        <tr v-if="value.excess && !value.excessWithGst">
          <td>Excess</td>
          <template v-if="showSupplementaryQuoteTotals">
            <td class="has-text-right has-text-danger">- {{ $filters.formatCurrency(value.excess, $userInfo.locale) }}</td>
            <td v-for="(supp) in supplementaryQuotes"
              :key="supp.quoteId"
              class="has-text-right">-
            </td>
          </template>
          <td class="has-text-right has-text-danger">- {{ $filters.formatCurrency(value.excess, $userInfo.locale) }}</td>
        </tr>
        <tr :class="`summary-background-success-${$theme.name}`">
          <th>Total Payable <span class="subtext">(Inc GST)</span></th>
          <template v-if="showSupplementaryQuoteTotals">
            <th class="has-text-right">{{ $filters.formatCurrency(mainQuoteTotalPayable, $userInfo.locale) }}</th>
            <th v-for="(supp) in supplementaryQuotes"
              :key="supp.quoteId"
              class="has-text-right">
              {{ $filters.formatCurrency(supp.totalIncGst, $userInfo.locale) }}
            </th>
          </template>
          <th class="has-text-right">{{ $filters.formatCurrency(totalPayableIncGst, $userInfo.locale) }}</th>
        </tr>
      </tfoot>
      <tbody v-if="value.subQuoteNos.length === 1 || (showSupplementaryQuoteTotals) || value.subQuoteNo > 0"
        class="has-bottom-border-only no-border-last-row">
        <tr v-if="isEnabled(itemCategoryTypes.RR)">
          <td>{{ getDisplayName(itemCategoryTypes.RR) }}</td>
          <td class="has-text-right">{{ $filters.formatNumber(value.rrTotal, $userInfo.locale) }}</td>
          <template v-if="showSupplementaryQuoteTotals">
            <td v-for="(supp) in supplementaryQuotes"
              :key="supp.quoteId"
              class="has-text-right">
              {{ $filters.formatNumber(supp.rrTotal, $userInfo.locale) }}
            </td>
            <td class="has-text-right">{{ $filters.formatNumber(calculateGrandTotal(value.rrTotal, supplementaryQuotes.map(q => q.rrTotal)), $userInfo.locale) }}</td>
          </template>
        </tr>
        <tr v-if="isEnabled(itemCategoryTypes.RWA)">
          <td>{{ getDisplayName(itemCategoryTypes.RWA) }}</td>
          <td class="has-text-right">{{ $filters.formatNumber(value.rwaTotal, $userInfo.locale) }}</td>
          <template v-if="showSupplementaryQuoteTotals">
            <td v-for="(supp) in supplementaryQuotes"
              :key="supp.quoteId"
              class="has-text-right">
              {{ $filters.formatNumber(supp.rwaTotal, $userInfo.locale) }}
            </td>
            <td class="has-text-right">{{ $filters.formatNumber(calculateGrandTotal(value.rwaTotal, supplementaryQuotes.map(q => q.rwaTotal)), $userInfo.locale) }}</td>
          </template>
        </tr>
        <tr v-if="isEnabled(itemCategoryTypes.REP)">
          <td>{{ getDisplayName(itemCategoryTypes.REP) }}</td>
          <td class="has-text-right">{{ $filters.formatNumber(value.repairTotal, $userInfo.locale) }}</td>
          <template v-if="showSupplementaryQuoteTotals">
            <td v-for="(supp) in supplementaryQuotes"
              :key="supp.quoteId"
              class="has-text-right">
              {{ $filters.formatNumber(supp.repairTotal, $userInfo.locale) }}
            </td>
            <td class="has-text-right">{{ $filters.formatNumber(calculateGrandTotal(value.repairTotal, supplementaryQuotes.map(q => q.repairTotal)), $userInfo.locale) }}</td>
          </template>
        </tr>
        <tr v-if="isEnabled(itemCategoryTypes.PAINT)">
          <td>{{ getDisplayName(itemCategoryTypes.PAINT) }}</td>
          <td class="has-text-right">{{ $filters.formatNumber(value.paintTotal, $userInfo.locale) }}</td>
          <template v-if="showSupplementaryQuoteTotals">
            <td v-for="(supp) in supplementaryQuotes"
              :key="supp.quoteId"
              class="has-text-right">
              {{ $filters.formatNumber(supp.paintTotal, $userInfo.locale) }}
            </td>
            <td class="has-text-right">{{ $filters.formatNumber(calculateGrandTotal(value.paintTotal, supplementaryQuotes.map(q => q.paintTotal)), $userInfo.locale) }}</td>
          </template>
        </tr>
        <tr v-if="isEnabled(itemCategoryTypes.FIBER)">
          <td>{{ getDisplayName(itemCategoryTypes.FIBER) }}</td>
          <td class="has-text-right">{{ $filters.formatNumber(value.fgTotal, $userInfo.locale) }}</td>
          <template v-if="showSupplementaryQuoteTotals">
            <td v-for="(supp) in supplementaryQuotes"
              :key="supp.quoteId"
              class="has-text-right">
              {{ $filters.formatNumber(supp.fgTotal, $userInfo.locale) }}
            </td>
            <td class="has-text-right">{{ $filters.formatNumber(calculateGrandTotal(value.fgTotal, supplementaryQuotes.map(q => q.fgTotal)), $userInfo.locale) }}</td>
          </template>
        </tr>
        <tr v-if="isEnabled(itemCategoryTypes.CRUSH)">
          <td>{{ getDisplayName(itemCategoryTypes.CRUSH) }}</td>
          <td class="has-text-right">{{ $filters.formatNumber(value.crushTotal, $userInfo.locale) }}</td>
          <template v-if="showSupplementaryQuoteTotals">
            <td v-for="(supp) in supplementaryQuotes"
              :key="supp.quoteId"
              class="has-text-right">
              {{ $filters.formatNumber(supp.crushTotal, $userInfo.locale) }}
            </td>
            <td class="has-text-right">{{ $filters.formatNumber(calculateGrandTotal(value.crushTotal, supplementaryQuotes.map(q => q.crushTotal)), $userInfo.locale) }}</td>
          </template>
        </tr>
        <tr v-if="isEnabled(itemCategoryTypes.CD)">
          <td>{{ getDisplayName(itemCategoryTypes.CD) }}</td>
          <td class="has-text-right">{{ $filters.formatNumber(value.cdTotal, $userInfo.locale) }}</td>
          <template v-if="showSupplementaryQuoteTotals">
            <td v-for="(supp) in supplementaryQuotes"
              :key="supp.quoteId"
              class="has-text-right">
              {{ $filters.formatNumber(supp.cdTotal, $userInfo.locale) }}
            </td>
            <td class="has-text-right">{{ $filters.formatNumber(calculateGrandTotal(value.cdTotal, supplementaryQuotes.map(q => q.cdTotal)), $userInfo.locale) }}</td>
          </template>
        </tr>
        <tr v-if="isEnabled(itemCategoryTypes.MECH)">
          <td>{{ getDisplayName(itemCategoryTypes.MECH) }}</td>
          <td class="has-text-right">{{ $filters.formatNumber(value.mechTotal, $userInfo.locale) }}</td>
          <template v-if="showSupplementaryQuoteTotals">
            <td v-for="(supp) in supplementaryQuotes"
              :key="supp.quoteId"
              class="has-text-right">
              {{ $filters.formatNumber(supp.mechTotal, $userInfo.locale) }}
            </td>
            <td class="has-text-right">{{ $filters.formatNumber(calculateGrandTotal(value.mechTotal, supplementaryQuotes.map(q => q.mechTotal)), $userInfo.locale) }}</td>
          </template>
        </tr>
        <tr v-if="isEnabled(itemCategoryTypes.PART)">
          <td>{{ getDisplayName(itemCategoryTypes.PART) }}</td>
          <td class="has-text-right">{{ $filters.formatNumber(value.partTotal, $userInfo.locale) }}</td>
          <template v-if="showSupplementaryQuoteTotals">
            <td v-for="(supp) in supplementaryQuotes"
              :key="supp.quoteId"
              class="has-text-right">
              {{ $filters.formatNumber(supp.partTotal, $userInfo.locale) }}
            </td>
            <td class="has-text-right">{{ $filters.formatNumber(calculateGrandTotal(value.partTotal, supplementaryQuotes.map(q => q.partTotal)), $userInfo.locale) }}</td>
          </template>
        </tr>
        <tr v-if="isEnabled(itemCategoryTypes.OPG)">
          <td>{{ getDisplayName(itemCategoryTypes.OPG) }}</td>
          <td class="has-text-right">{{ $filters.formatNumber(value.opgTotal, $userInfo.locale) }}</td>
          <template v-if="showSupplementaryQuoteTotals">
            <td v-for="(supp) in supplementaryQuotes"
              :key="supp.quoteId"
              class="has-text-right">
              {{ $filters.formatNumber(supp.opgTotal, $userInfo.locale) }}
            </td>
            <td class="has-text-right">{{ $filters.formatNumber(calculateGrandTotal(value.opgTotal, supplementaryQuotes.map(q => q.opgTotal)), $userInfo.locale) }}</td>
          </template>
        </tr>
        <tr v-if="isEnabled(itemCategoryTypes.MISC)">
          <td>{{ getDisplayName(itemCategoryTypes.MISC) }}</td>
          <td class="has-text-right">{{ $filters.formatNumber(value.miscTotal, $userInfo.locale) }}</td>
          <template v-if="showSupplementaryQuoteTotals">
            <td v-for="(supp) in supplementaryQuotes"
              :key="supp.quoteId"
              class="has-text-right">
              {{ $filters.formatNumber(supp.miscTotal, $userInfo.locale) }}
            </td>
            <td class="has-text-right">{{ $filters.formatNumber(calculateGrandTotal(value.miscTotal, supplementaryQuotes.map(q => q.miscTotal)), $userInfo.locale) }}</td>
          </template>
        </tr>
        <tr v-if="isEnabled(itemCategoryTypes.SUBL)">
          <td>{{ getDisplayName(itemCategoryTypes.SUBL) }}</td>
          <td class="has-text-right">{{ $filters.formatNumber(value.sublTotal, $userInfo.locale) }}</td>
          <template v-if="showSupplementaryQuoteTotals">
            <td v-for="(supp) in supplementaryQuotes"
              :key="supp.quoteId"
              class="has-text-right">
              {{ $filters.formatNumber(supp.sublTotal, $userInfo.locale) }}
            </td>
            <td class="has-text-right">{{ $filters.formatNumber(calculateGrandTotal(value.sublTotal, supplementaryQuotes.map(q => q.sublTotal)), $userInfo.locale) }}</td>
          </template>
        </tr>
      </tbody>
    </table>
    <div v-if="displayRemarks.length > 0">
      <label v-if="hasPinnedRemarks"
        class="label">Pinned Remarks</label>
      <label v-if="!hasPinnedRemarks"
        class="label">Recent Remarks</label>
      <table v-if="value.subQuoteNos.length === 1 || (showSupplementaryQuoteTotals) || value.subQuoteNo > 0"
        class="table is-bordered is-striped is-narrow is-fullwidth">
        <thead />
        <tbody>
          <tr v-for="(remark) in displayRemarks"
            :key="remark.remarkId">
            <td class="is-content-width">
              <span class="tag is-info">{{ $filters.formatDateTimeLocale(remark.remarkDateTime, $userInfo.locale) }}</span>
            </td>
            <td class="is-remark">
              <div>{{ remark.remarks }}</div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { RemarkTypes, ItemCategoryTypes } from '@/enums'
import StoreMixin from './storeMixin'
import QuoteService from './QuoteService'
import { roundAwayFromZero } from '@/components/utils/AccountingFunctions'

export default {
  name: 'QuoteDetailSummary',
  mixins: [StoreMixin],
  props: {
    value: null
  },
  data() {
    return {
      supplementaryQuotes: [],
      isFetchingSupplementaryQuote: false
    }
  },
  computed: {
    hasPinnedRemarks() {
      return this.pinnedActiveRemarks && this.pinnedActiveRemarks.length > 0
    },
    pinnedActiveRemarks() {
      const pinnedActiveRemarks = this.value.remarks.filter(
        r => r.pinRemarkOrder !== null && r.remarkTypeId === RemarkTypes.Remark && !r.isDeleted)
      return pinnedActiveRemarks.sort((a, b) => {
        return a.pinRemarkOrder - b.pinRemarkOrder
      })
    },
    displayRemarks() {
      const unpinnedActiveRemarks = this.value.remarks.filter(
        r => r.pinRemarkOrder == null && r.remarkTypeId === RemarkTypes.Remark && !r.isDeleted)
      unpinnedActiveRemarks.sort((a, b) => {
        return new Date(b.remarkDateTime) - new Date(a.remarkDateTime)
      })
      return this.hasPinnedRemarks ? this.pinnedActiveRemarks.slice(0, 5) : unpinnedActiveRemarks.slice(0, 5)
    },
    itemCategoryTypes() {
      return ItemCategoryTypes
    },
    showSupplementaryQuoteTotals() {
      return this.value.subQuoteNos.length > 1 && this.value.subQuoteNo === 0 && !this.isFetchingSupplementaryQuote && this.supplementaryQuotes.length > 0
    },
    test() {
      return this.supplementaryQuotes.map((q) => q.rwaTotal)
    },
    grandTotalExGst() {
      return this.showSupplementaryQuoteTotals ?
        this.calculateGrandTotal(this.value.totalExGst, this.supplementaryQuotes.map(q => q.totalExGst)) : this.value.totalExGst
    },
    invTotalExGst() {
      const excess = this.value.excess ?? 0
      const discount = this.value.discount ?? 0

      return !this.value.excessWithGst ? this.grandTotalExGst - discount : this.grandTotalExGst - (excess + discount)
    },
    totalPayableIncGst() {
      const total = this.invTotalExGst * (this.value.gstRate/100 + 1)
      const ownerContributionWithGST = this.value.ownersContribution * (this.value.gstRate/100 + 1)
      const res =  this.value.excessWithGst ? total - ownerContributionWithGST : total - this.value.excess - ownerContributionWithGST
      return res
    },
    showInvAmtIncGst() {
      return this.value.ownersContribution || this.value.excess || this.value.discount
    },
    mainQuoteTotalGst() {
      const grandSuppTotalExGst = this.supplementaryQuotes.reduce((sum, supp) => {
        return sum + (parseFloat(supp.totalExGst) || 0)
      }, 0)
      return (this.invTotalExGst - grandSuppTotalExGst) * (this.value.gstRate/100)
    },
    mainQuoteTotalIncGst() {
      const grandSuppTotalIncGst = this.supplementaryQuotes.reduce((sum, supp) => {
        return sum + (parseFloat(supp.totalIncGst) || 0)
      }, 0)
      return (this.invTotalExGst * (this.value.gstRate/100 + 1) - grandSuppTotalIncGst)
    },
    mainQuoteTotalPayable() {
      const grandSuppTotalIncGst = this.supplementaryQuotes.reduce((sum, supp) => {
        return sum + (parseFloat(supp.totalIncGst) || 0)
      }, 0)
      return (this.totalPayableIncGst - grandSuppTotalIncGst)
    }
  },
  created() {
    this.getSupplementaryQuoteTotals()
  },
  mounted() {},
  methods: {
    getDisplayName(itemCategoryType) {
      if (this.companyItemTypes) {
        const itemType = this.companyItemTypes.find((i) => i.itemCategoryType === itemCategoryType)
        if (itemType) {
          return itemType.displayName
        }
      }
      return itemCategoryType
    },
    isEnabled(itemCategoryType) {
      if (this.companyItemTypes) {
        const itemType = this.companyItemTypes.find((i) => i.itemCategoryType === itemCategoryType)
        if (itemType) {
          return itemType.enabled
        }
      }
      return false
    },
    async getSupplementaryQuoteTotals() {
      if (this.value.subQuoteNo === 0) {
        this.isFetchingSupplementaryQuote = true
        this.supplementaryQuotes = await QuoteService.getSupplementaryQuoteTotals(this.value.quoteId)
        this.supplementaryQuotes.sort((a, b) => a.subQuoteNo - b.subQuoteNo)
      }
      this.isFetchingSupplementaryQuote = false
    },
    calculateGrandTotal(mainCategorytotal, supplementaryCategoryTotalsArray) {
      const suppCategoryTotal = supplementaryCategoryTotalsArray.reduce(function (total, suppTotal) {
        return total + suppTotal
      }, 0)
      return mainCategorytotal + suppCategoryTotal
    },
    calculateSummaryTotal() {
      const suppTotalExGst = this.supplementaryQuotes
        .map((q) => q.totalExGst)
        .reduce(function (total, suppTotal) {
          return roundAwayFromZero(total + suppTotal)
        }, 0)
      const gstTotal = roundAwayFromZero((this.value.totalExGst + suppTotalExGst) * (this.value.gstRate / 100))
      const totalExGst = roundAwayFromZero(this.value.totalExGst + suppTotalExGst)
      return roundAwayFromZero(totalExGst + gstTotal)
    }
  }
}
</script>

<style lang="scss" scoped>
th.is-subtotal {
  width: 7em;
}

.subtext {
  font-size: 10px;
}

.no-border td,
.no-border th {
  border: none;
}

.has-bottom-border-only td {
  border-top: none;
  border-left: none;
  border-right: none;
}

.no-border-last-row tr:last-child td {
  border: none;
}
</style>
